import { useState } from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  Input,
  Button,
  Flex,
  Icon,
  Badge,
  SimpleGrid,
  Card,
  CardBody,
  useColorMode,
  useToast,
  FormControl,
  FormLabel,
} from "@chakra-ui/react";
import {
  FaVideo,
  FaCoins,
  FaLock,
  FaBrain,
  FaRocket,
  FaCheckCircle,
} from "react-icons/fa";
import { useMutation, gql } from "@apollo/client";
import { useNavigate, useSearchParams } from "react-router-dom";
import { NavbarWithCallToAction } from "../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { getColor } from "../../brandConfig";

const REGISTER_FOR_EVENT = gql`
  mutation RegisterForEvent($input: RegisterForEventInput!) {
    registerForEvent(input: $input) {
      success
      message
      registration {
        id
        email
        firstName
      }
    }
  }
`;

const EVENT_ID = "bb-webinar-business-sovereignty";
const EVENT_NAME = "Business Sovereignty: Own Your Data, Train Your AI, Scale Your Way";

const RegisterPage = () => {
  const { colorMode } = useColorMode();
  const toast = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
  });
  const [registered, setRegistered] = useState(false);

  const bg = getColor("background.main", colorMode);
  const cardGradientBg =
    colorMode === "light"
      ? "rgba(255,255,255,0.95)"
      : "rgba(20,20,20,0.8)";
  const textPrimary = colorMode === "light" ? "gray.800" : "white";
  const textSecondary = colorMode === "light" ? "gray.600" : "gray.300";

  const [registerForEvent, { loading }] = useMutation(REGISTER_FOR_EVENT);

  const handleSubmit = async () => {
    if (!form.email || !form.firstName) {
      toast({ title: "Please fill in your name and email", status: "warning", duration: 3000 });
      return;
    }

    try {
      const { data } = await registerForEvent({
        variables: {
          input: {
            eventId: EVENT_ID,
            eventName: EVENT_NAME,
            email: form.email,
            firstName: form.firstName,
            lastName: form.lastName,
            phone: form.phone,
            source: "REGISTER_PAGE",
            referredByEmail: searchParams.get("ref") || undefined,
            utmSource: searchParams.get("utm_source") || undefined,
            utmMedium: searchParams.get("utm_medium") || undefined,
            utmCampaign: searchParams.get("utm_campaign") || undefined,
          },
        },
      });

      if (data?.registerForEvent?.success) {
        setRegistered(true);
        toast({ title: "You're registered!", status: "success", duration: 5000 });
      }
    } catch (error) {
      toast({ title: "Something went wrong. Try again.", status: "error", duration: 3000 });
    }
  };

  const highlights = [
    { icon: FaLock, text: "Why your current SaaS is making you dependent" },
    { icon: FaBrain, text: "How to train AI on YOUR business data" },
    { icon: FaRocket, text: "The Business Builder sovereignty roadmap" },
    { icon: FaCoins, text: "How BB Tokens let you earn while you learn" },
  ];

  return (
    <Box bg={bg} minHeight="100vh">
      <NavbarWithCallToAction />

      <Container maxW="1200px" py={16}>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={10} alignItems="start">
          {/* Left - Info */}
          <VStack spacing={6} align="start" pt={4}>
            <Badge
              bg="linear-gradient(135deg, #9333EA 0%, #7C3AED 100%)"
              color="white"
              fontSize="md"
              px={5}
              py={2}
              borderRadius="full"
              fontWeight="600"
            >
              <Icon as={FaVideo} mr={2} />
              Free Live Webinar
            </Badge>

            <Heading
              as="h1"
              fontSize={{ base: "2xl", md: "4xl" }}
              fontWeight="800"
              color={textPrimary}
              lineHeight="1.2"
            >
              Business Sovereignty:{" "}
              <Text as="span" bgGradient="linear(to-r, #9333EA, #F59E0B)" bgClip="text">
                Own Your Data, Train Your AI
              </Text>
            </Heading>

            <Text fontSize="lg" color={textSecondary} lineHeight="1.7">
              Join Tom Miller for a live session on breaking free from platform
              dependence and building a business you truly own.
            </Text>

            <VStack spacing={3} align="start" w="100%">
              {highlights.map((item, i) => (
                <Flex key={i} align="center" gap={3}>
                  <Icon as={FaCheckCircle} color="#10B981" />
                  <Text color={textSecondary}>{item.text}</Text>
                </Flex>
              ))}
            </VStack>

            <Card
              bg="rgba(245, 158, 11, 0.1)"
              borderWidth="1px"
              borderColor="rgba(245, 158, 11, 0.3)"
              borderRadius="xl"
              w="100%"
            >
              <CardBody>
                <Flex align="center" gap={3}>
                  <Icon as={FaCoins} w={8} h={8} color="#F59E0B" />
                  <Box>
                    <Text fontWeight="700" color={textPrimary}>
                      Earn 1 BB Token (AU$50) for registering
                    </Text>
                    <Text fontSize="sm" color={textSecondary}>
                      Plus up to 4 more tokens during the live event!
                    </Text>
                  </Box>
                </Flex>
              </CardBody>
            </Card>
          </VStack>

          {/* Right - Form */}
          <Card
            bg={cardGradientBg}
            borderRadius="2xl"
            borderWidth="2px"
            borderColor="rgba(147, 51, 234, 0.3)"
            boxShadow="0 12px 40px rgba(147, 51, 234, 0.2)"
          >
            <CardBody p={8}>
              {!registered ? (
                <VStack spacing={5}>
                  <Heading size="lg" color={textPrimary} textAlign="center">
                    Reserve Your Spot
                  </Heading>
                  <Text color={textSecondary} textAlign="center" fontSize="sm">
                    Limited spots available. Register now to secure yours.
                  </Text>

                  <FormControl>
                    <FormLabel color={textSecondary} fontSize="sm">First Name *</FormLabel>
                    <Input
                      value={form.firstName}
                      onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                      placeholder="Tom"
                      size="lg"
                      borderColor="rgba(147, 51, 234, 0.3)"
                      _focus={{ borderColor: "#9333EA" }}
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel color={textSecondary} fontSize="sm">Last Name</FormLabel>
                    <Input
                      value={form.lastName}
                      onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                      placeholder="Miller"
                      size="lg"
                      borderColor="rgba(147, 51, 234, 0.3)"
                      _focus={{ borderColor: "#9333EA" }}
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel color={textSecondary} fontSize="sm">Email Address *</FormLabel>
                    <Input
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      placeholder="tom@example.com"
                      type="email"
                      size="lg"
                      borderColor="rgba(147, 51, 234, 0.3)"
                      _focus={{ borderColor: "#9333EA" }}
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel color={textSecondary} fontSize="sm">Phone (optional)</FormLabel>
                    <Input
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      placeholder="+61 4XX XXX XXX"
                      size="lg"
                      borderColor="rgba(147, 51, 234, 0.3)"
                      _focus={{ borderColor: "#9333EA" }}
                    />
                  </FormControl>

                  <Button
                    size="lg"
                    w="100%"
                    bg="linear-gradient(135deg, #9333EA 0%, #7C3AED 100%)"
                    color="white"
                    _hover={{
                      transform: "translateY(-2px)",
                      boxShadow: "0 8px 24px rgba(147, 51, 234, 0.5)",
                    }}
                    py={7}
                    fontSize="lg"
                    isLoading={loading}
                    onClick={handleSubmit}
                  >
                    Register Now — It's Free
                  </Button>

                  <Text fontSize="xs" color="gray.500" textAlign="center">
                    By registering you'll also earn 1 BB Token (AU$50 value).
                    No credit card required.
                  </Text>
                </VStack>
              ) : (
                <VStack spacing={6} py={8}>
                  <Text fontSize="5xl">🎉</Text>
                  <Heading size="lg" color={textPrimary} textAlign="center">
                    You're Registered!
                  </Heading>
                  <Text color={textSecondary} textAlign="center">
                    Check your email for confirmation and the link to join live.
                  </Text>
                  <Badge colorScheme="green" fontSize="md" px={4} py={2} borderRadius="full">
                    +1 BB Token Earned (AU$50)
                  </Badge>
                  <Text fontSize="sm" color={textSecondary} textAlign="center">
                    Attend live to earn even more tokens!
                  </Text>
                </VStack>
              )}
            </CardBody>
          </Card>
        </SimpleGrid>
      </Container>

      <FooterWithFourColumns />
    </Box>
  );
};

export default RegisterPage;
